import { Response } from 'express';
import { Packages } from '~shared/constants/pricing';
import { ExpCookie, ExpResult } from '~shared/types/pricing/experiments';
/**
 * Checks if we are in a browser
 * @returns boolean
 */
export const EMPTY_STRING = '';

/**
 * Check if we are in the DOM or browser
 * @returns boolean
 */
export const isBrowser = (): boolean => typeof window === 'object';

/**
 * See https://docs.google.com/document/d/1a6JLyH4V7C6Wipl28lJa84yeaqqvUINfrBLmUQ3lNLA/
 * See https://momentiveai.atlassian.net/wiki/spaces/GROW/pages/977240306/Amplitude+Naming+Conventions
 */
export const AMPLITUDE_PAGE_EVENTS: Record<string, string> = Object.freeze({
  pageView: 'page viewed',
  selectedPlans: 'pricing selected plan',
  experimentEvent: 'experiment event',
});

/**
 * https://momentiveai.atlassian.net/browse/GROW-2234
 * Some canonical name does not match what amplitude tracking expects
 * The object mapping resolves anything that is different from the canonical name
 * This can also serve as an override to existing packages. Please do so with authorazation
 */
export const AMPLITUDE_SELECTED_PACKAGE_MAP: Partial<Record<Packages, string>> = Object.freeze({
  [Packages.Enterprise]: 'enterprise_platinum',
});

/**
 * A utility function that converts arrays to object like
 * @param keys
 * @returns object
 */

export function convertToObject<K extends string>(...keys: K[]): { [T in K]: string } {
  const tempObject = {} as { [T in K]: string };
  return keys.reduce((a, v) => ({ ...a, [v]: v }), tempObject);
}

/**
 * This is a modified clone of
 * https://github.com/mntv-webplatform/smpackages/blob/97fcc41a6808c4e2b867dc35d54b2b5abc21279c/packages/metrics/src/utils.ts#L9
 * @param cname
 * @param defaultValue
 * @returns The cookie value we are interested in
 */
export function getCookieValue(cname: string): string {
  if (!isBrowser()) return '';
  const name = `${cname}=`;
  let cookieValue = '';
  const decodedCookie = decodeURIComponent(document.cookie);
  const decodedCookies = decodedCookie.split(';');

  decodedCookies?.forEach((c: string) => {
    let cSub = c;
    while (cSub.startsWith(' ')) {
      cSub = c.substring(1);
    }
    if (cSub.startsWith(name)) {
      cookieValue = c.substring(name.length + 1, c.length);
    }
  });
  return cookieValue;
}
/**
 * We need to set smexp cookie for logged-out experiments to ensure smooth experience from page to page
 * The smexp cookie value has the format {experiment_id}.{treatment_id}.{datetime_created}|{experiment_id2}.{treatment_id2}.{datetime_created2}
 * Since we don't want to take any chances we will set every cookie expsvc returns except the preview cookie.
 * @param res
 * @param cookies
 */
export function setExperimentCookies(res: Response, cookies: ExpCookie[] | undefined, hostname: string): void {
  const remappedCookies = cookies?.reduce((acc: ExpResult, { name, value, age }: ExpCookie) => {
    return {
      ...acc,
      [name]: {
        value: acc[name]?.value ? `${acc[name]?.value}|${value}` : value,
        age,
      },
    };
  }, {});
  if (remappedCookies) {
    Object.keys(remappedCookies).forEach((name: string) => {
      const { value, age } = remappedCookies[name];
      const removeDeDupsCookieValue = Array.from(new Set(value.split('|'))).join('|');
      res.cookie(name, removeDeDupsCookieValue, {
        domain: hostname?.replace('www', ''), // surveymonkey ttld is preversed with this approach
        expires: new Date(age * 1000 + Date.now()),
        secure: true,
        path: '/',
        encode: tempValue => tempValue,
      });
    });
  }
}

export { convertToObject as default };
